import {
  Box, Button, FormControl,
  FormErrorMessage,
  Radio,
  RadioGroup,
} from '@chakra-ui/react'
import type { InsuranceParamsPropertyDto, Property } from 'BrokerTsLib/src/dtos'
import { Product, Risk, RiskVariant } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import _ from 'lodash'
import * as yup from 'yup'

import { useApp } from '@/hooks/useApp'
import { FormLabel } from '../../components/FormLabel'
import { FormSpacer } from '../../components/FormSpacer'
import { InsuranceCompanies } from '../../components/InsuranceCompanies'
import { RadioStack } from '../../components/RadioStack'
import { usePropertyOffersContext } from '../../context/PropertyOffersProvider'
import { useInsurers } from '../../hooks'
import { useNameof } from '../../hooks/useNameof'
import { useAddVariant } from '../../hooks/useWizard'
import { Add } from '../../icons'
import { useInsuranceParamsContext } from '../../layouts/Wizard'
import { scrollToOfferWithOffset } from '../offers/OfferCar'
import { PropertyTypeSelector } from './PropertyInsuranceTypeSelector'

export const NewInsuranceSettings = () => {
  const nameof = useNameof<Property>()
  const { product } = useApp()

  const { insuranceParams: insuranceParamsGeneric, insuranceParamsId, refetchInsuranceParams } = useInsuranceParamsContext() ?? {}
  const insuranceParams = insuranceParamsGeneric as InsuranceParamsPropertyDto
  const { refetchOffers: getOffers } = usePropertyOffersContext() ?? {}
  const { mutateAsync: createVariant } = useAddVariant(insuranceParamsId!)
  const { data: allInsurers, isLoading: isLoadingInsurers, isFetching: isFetchingInsurers } = useInsurers(false, null)

  const methods = useForm<Property>({
    defaultValues: insuranceParams.property,
    resolver: yupResolver(yup.object().shape({})) as any, // aby se pripadne resetovaly serverove validace
    mode: 'onBlur',
  })

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = methods

  const onSubmitForm = handleSubmit((values: Property) =>
    createVariant(_.cloneDeep(values))
      .then(() => {
        refetchInsuranceParams()
        // getOffers()
        scrollToOfferWithOffset('steps')
      }))

  const hasHouseholdAndRealtyVariant = !!insuranceParams.property.variants
    ?.find(x => x.variantParts
      ?.find(x => x.risk == Risk.PROPERTY_TYPE)
      ?.riskVariant == RiskVariant.HouseholdAndRealty
    )

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmitForm}>
        <Box sx={{ display: 'grid', gap: 4, placeItems: 'start' }}>
          {product == Product.PropertyInsurance && <>

            {hasHouseholdAndRealtyVariant &&
              <FormControl isInvalid={!!errors.type}>
                <FormLabel>Typ pojištění</FormLabel>

                <PropertyTypeSelector prefix={nameof(x => x.type)} />

                {errors.type && <FormErrorMessage>{errors.type.message}</FormErrorMessage>}
              </FormControl>
            }

            <FormSpacer />

            <FormControl isInvalid={!!errors.range}>
              <FormLabel>Rozsah pojištění</FormLabel>

              <RadioGroup
                onChange={(x) => setValue('range', parseInt(x))}
                value={watch('range')?.toString()}
              >
                <RadioStack variant="stackedTile">
                  <Radio value={RiskVariant.MiniRange.toString()}>Nižší</Radio>
                  <Radio value={RiskVariant.MaxiRange.toString()}>Vyšší</Radio>
                </RadioStack>
              </RadioGroup>

              {errors.range && <FormErrorMessage>{errors.range.message}</FormErrorMessage>}
            </FormControl>

            <FormSpacer />
          </>
          }

          <FormControl isInvalid={!!errors.insurers}>
            <InsuranceCompanies
              companies={allInsurers ?? []}
              isLoadingCompanies={(isFetchingInsurers || isLoadingInsurers)}
              propPath={nameof(x => x.insurers)}
              tooltip="Vyberte pojišťovny, které chcete zahrnout do kalkulace pojištění." />

            {errors.insurers && <FormErrorMessage>{errors.insurers.message}</FormErrorMessage>}
          </FormControl>

          <Button type="submit" variant="outline" isLoading={isSubmitting} isDisabled={isSubmitting}>
            <Add color="secondary.500" />
            Přidat do kalkulace
          </Button>
        </Box>
      </form>
    </FormProvider>
  )
}
